import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, PromptLine, pick, shuffle, useGame, useRoundTimer, type GameProps } from '../sdk/react';
import { findPrompt } from '../phrases';
import { MODEL_CHOICES } from '../scenes/catalog';
import { KINDS } from '../../concepts/kinds';
import type { ModelHandle } from '../three/models3d';

export default function RotateFind(props: GameProps) {
  const game = useGame(props);
  const model = MODEL_CHOICES.find((m) => m.id === game.config?.scene) ?? null;
  const wanted = Array.isArray(game.config?.parts) ? (game.config!.parts as string[]) : [];
  const parts = model ? model.parts.filter((p) => wanted.includes(p.id)) : [];
  const hostRef = useRef<HTMLDivElement | null>(null);
  const handle = useRef<ModelHandle | null>(null);
  const [failed, setFailed] = useState(false);
  const [target, setTarget] = useState<{ id: string; label: string } | null>(null);
  const [trial, setTrial] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const queue = useRef<{ id: string; label: string }[]>([]);
  const busy = useRef(false);
  const live = useRef({ target, trial, done });
  live.current = { target, trial, done };
  const moving = !game.settings.reduceMotion;

  const start = useCallback(async () => {
    if (!model || parts.length === 0) return;
    if (queue.current.length === 0) queue.current = shuffle(parts);
    const next = queue.current.shift() ?? pick(parts);
    setDone(false);
    const id = await game.register({ model: model.id, find: next.id }, { value: next.id });
    if (!id) return;
    setTarget(next);
    setTrial(id);
  }, [model?.id, wanted.join()]);

  useEffect(() => { if (game.config) void start(); }, [game.config, start]);

  const prompt = target ? findPrompt(target.label) : '';
  useEffect(() => {
    if (!target || game.textPrompt) return;
    const t = window.setTimeout(() => game.say(prompt), 300);
    return () => window.clearTimeout(t);
  }, [target, trial]);

  const pickPart = useCallback(async (id: string) => {
    const { target: tg, trial: tr, done: dn } = live.current;
    if (!tg || !tr || dn || busy.current) return;
    if (id !== tg.id && game.errorless) return;
    busy.current = true;
    const v = await game.respond(tr, id);
    if (v?.correct) {
      handle.current?.flash(id);
      setDone(true);
      busy.current = false;
      return;
    }
    game.say(findPrompt(tg.label));
    const fresh = await game.register({ model: model!.id, find: tg.id, retry: true }, { value: tg.id });
    busy.current = false;
    if (fresh) setTrial(fresh);
  }, [model?.id, game.errorless]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !model || failed) return;
    let cancelled = false;
    const canvas = document.createElement('canvas');
    canvas.className = 'g-canvas3d';
    canvas.setAttribute('aria-label', `${model.label} you can turn round`);
    host.appendChild(canvas);
    const turns = game.str('startView', 'front') === 'random' ? Math.floor(Math.random() * 4) : 0;
    void import('../three/models3d').then(({ mountModel }) => mountModel(canvas, model.id, turns, moving, {
      onPick: (id) => { void pickPart(id); },
      onHover: () => {},
      onLost: () => { if (!cancelled) setFailed(true); },
    })).then((h) => {
      if (cancelled) { h.dispose(); return; }
      handle.current = h;
    }).catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; handle.current?.dispose(); handle.current = null; canvas.remove(); };
  }, [model?.id, failed, !!game.config]);

  useEffect(() => { handle.current?.setMoving(moving); }, [moving]);

  const fading = useRoundTimer(done, game.transitionMs, () => { void start(); }, 1300);

  return (
    <GameFrame game={game} empty={!model || parts.length === 0} emptyText={`${KINDS.rotateFind.describes} Edit it to choose an object and its parts.`} onResume={() => { void start(); }} breakTrial={trial}>
      {model && (
        <div className={`g-board g-rotate${fading ? ' is-fading' : ''}`}>
          <PromptLine game={game} text={prompt} />
          {failed ? (
            <p className="field-hint">The 3D picture could not be shown on this device.</p>
          ) : (
            <div className="g-stage3d" ref={hostRef} />
          )}
          <div className="g-turn-row">
            <button type="button" className="btn secondary g-turn" onClick={() => handle.current?.turn(1)} aria-label="Turn left"><span aria-hidden="true">⟲</span> Turn</button>
            <button type="button" className="btn secondary g-turn" onClick={() => handle.current?.turn(-1)} aria-label="Turn right">Turn <span aria-hidden="true">⟳</span></button>
          </div>
          {!failed && (
            <div className="g-part-keys visually-hidden" role="group" aria-label="Parts, for keyboard users">
              {model.parts.map((p) => (
                <button key={p.id} type="button" className="g-part-key" onClick={() => void pickPart(p.id)}>{p.label}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </GameFrame>
  );
}
